import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/10"
          : "backdrop-blur-md bg-black/30 border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-2 group"
        >
          <div className="relative w-9 h-9 grid place-items-center rounded-md bg-[#00F0FF] text-black font-display font-bold text-lg cyan-glow">
            P
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg text-white tracking-tight">
              Pransam
            </div>
            <div className="font-mono-label text-[10px] text-[#00F0FF]">
              TECHNOLOGIES LLP
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors duration-200 ${
                  isActive
                    ? "text-[#00F0FF]"
                    : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF] text-black text-sm font-semibold px-5 py-2.5 transition-transform duration-200 hover:-translate-y-0.5 hover-glow"
          >
            Start a project
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          className="lg:hidden text-white p-2 rounded-md hover:bg-white/10 transition-colors"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden backdrop-blur-xl bg-black/85 border-t border-white/10"
        >
          <div className="container-x py-6 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base ${
                    isActive
                      ? "text-[#00F0FF] bg-white/5"
                      : "text-white/80 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-3 inline-flex justify-center rounded-full bg-[#00F0FF] text-black font-semibold px-5 py-3"
            >
              Start a project
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
